import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import Spinner from './Spinner';

export interface Column<T> {
  /** 행 객체에서의 키 (정렬 기준이 됨) */
  key: string;
  header: ReactNode;
  /** 셀 렌더러 (없으면 row[key] 를 그대로 출력) */
  render?: (row: T, index: number) => ReactNode;
  /** 이 컬럼 정렬 허용 (기본: true) */
  sortable?: boolean;
  /** 셀 너비 */
  width?: string | number;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  /** 행 고유 키 추출 */
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  emptyText?: string;
  /** 행 클릭 핸들러 */
  onRowClick?: (row: T) => void;

  /**
   * 페이지네이션 (선택).
   * - 제공 시 페이저 표시. 서버 페이지네이션이면 onPageChange 로 외부 제어.
   */
  page?: number; // 0-based
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;

  /**
   * 정렬 (선택).
   * - 미지정 시 클라이언트 측 정렬을 내부 수행.
   * - sort/onSortChange 지정 시 서버 정렬로 외부 제어(내부 정렬 미수행).
   */
  sort?: { key: string; dir: 'asc' | 'desc' } | null;
  onSortChange?: (sort: { key: string; dir: 'asc' | 'desc' }) => void;
}

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'ko');
}

/** 정렬 + 페이지네이션을 지원하는 데이터 테이블 */
export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyText = '데이터가 없습니다.',
  onRowClick,
  page,
  pageSize,
  total,
  onPageChange,
  sort,
  onSortChange,
}: DataTableProps<T>) {
  // 외부 정렬 제어 여부
  const controlledSort = sort !== undefined || !!onSortChange;
  const [innerSort, setInnerSort] = useState<SortState>(null);
  const effectiveSort = controlledSort ? sort ?? null : innerSort;

  const handleSort = (col: Column<T>) => {
    if (col.sortable === false) return;
    const nextDir: 'asc' | 'desc' =
      effectiveSort?.key === col.key && effectiveSort.dir === 'asc' ? 'desc' : 'asc';
    const next = { key: col.key, dir: nextDir };
    if (controlledSort) onSortChange?.(next);
    else setInnerSort(next);
  };

  // 클라이언트 정렬 (외부 제어가 아닐 때만)
  const displayRows = useMemo(() => {
    if (controlledSort || !innerSort) return rows;
    const { key, dir } = innerSort;
    const sorted = [...rows].sort((a, b) =>
      compareValues((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
    );
    if (dir === 'desc') sorted.reverse();
    return sorted;
  }, [rows, innerSort, controlledSort]);

  const showPager = page !== undefined && pageSize !== undefined && total !== undefined;
  const pageCount = showPager ? Math.max(1, Math.ceil(total! / pageSize!)) : 1;
  const curPage = page ?? 0;
  const from = showPager ? curPage * pageSize! + (total! === 0 ? 0 : 1) : 0;
  const to = showPager ? Math.min(total!, (curPage + 1) * pageSize!) : 0;

  return (
    <div>
      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const sortable = col.sortable !== false;
                const active = effectiveSort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    className={sortable ? 'is-sortable' : ''}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={sortable ? () => handleSort(col) : undefined}
                    aria-sort={active ? (effectiveSort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {col.header}
                    {sortable && (
                      <span className="sort-arrow">
                        {active ? (effectiveSort!.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length}>
                  <Spinner center />
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td className="data-table__empty" colSpan={columns.length}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              displayRows.map((row, i) => (
                <tr
                  key={rowKey(row, i)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(row, i) : formatCell((row as Record<string, unknown>)[col.key])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPager && (
        <div className="data-table__pager">
          <span>
            {total! === 0 ? '0' : `${from}–${to}`} / 총 {total!.toLocaleString('ko-KR')}
          </span>
          <div className="data-table__pager-buttons">
            <button
              className="btn btn--secondary btn--sm"
              disabled={curPage <= 0 || loading}
              onClick={() => onPageChange?.(curPage - 1)}
            >
              이전
            </button>
            <span style={{ alignSelf: 'center' }}>
              {curPage + 1} / {pageCount}
            </span>
            <button
              className="btn btn--secondary btn--sm"
              disabled={curPage >= pageCount - 1 || loading}
              onClick={() => onPageChange?.(curPage + 1)}
            >
              다음
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatCell(v: unknown): ReactNode {
  if (v == null) return <span className="muted">—</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
